import { BodyM, Button, Heading2 } from '@casavo/base-ui'
import { motion } from 'framer-motion'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'

import { ContextValue } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { TokenSupport } from '@/design-lib/base-ui/Typography'
import { ButtonLink } from '@/design-lib/components/ButtonLink'
import { Divider } from '@/design-lib/components/Divider'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { ListingCate } from '@/page-commons/listingBySlug'
import { paddingBottom } from '@/shared-styles/styles.css'
import { CasavoInventory } from '@/types/listing'
import { anchorSmoothScroll } from '@/utils/dom-interactions-helpers'
import { isLocaleEnabledFor } from '@/utils/locale-variables'
import { handleClick } from '@/utils/tracking/eventsHandlers'

import { sellersFlowUrlPath } from '../../i18n-routes'
import { Translated } from '../Translated'

import SuccessIBuyerOrOda from './SuccessIBuyerOrOda'
import SuccessSaveSearch from './SuccessSaveSearch'

type Props = {
  cities: ContextValue
  listing: ListingCate
  onGoToSimilarProperties?: VoidFunction
  similarPropertiesLength: number
  userEmail?: string
}

const Success: React.FC<Props> = ({ cities, listing, onGoToSimilarProperties, similarPropertiesLength, userEmail }) => {
  const { lang, t } = useTranslation()
  const device = useDevice()
  const { publicRuntimeConfig } = getConfig()
  const ctaMinWidth = 230
  const isIBuyerOrOdaInventory =
    listing.detail.inventory === CasavoInventory.iBuyer || listing.detail.inventory === CasavoInventory.Agency

  const {
    publicRuntimeConfig: { imageBasePath },
  } = getConfig()

  const Illustration = (props) => (
    <img
      alt=""
      className={sprinkles({ marginBottom: 's', marginX: 'auto' })}
      height={150}
      loading="lazy"
      src={`${imageBasePath}/images/concepts/we-buy-or-sell.svg`}
      width={150}
      {...props}
    />
  )

  return (
    <motion.div
      animate={{ opacity: 1 }}
      className={sprinkles({
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        marginBottom: '3xl',
        textAlign: 'center',
      })}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      {isLocaleEnabledFor('saveSearch', lang) ? (
        <div className={sprinkles({ marginY: 'auto' })}>
          <Illustration className={sprinkles({ marginBottom: '2xl', marginX: 'auto' })} />
          <SuccessSaveSearch
            cities={cities}
            listing={listing}
            similarPropertiesLength={similarPropertiesLength}
            userEmail={userEmail}
          />
        </div>
      ) : (
        <>
          <div className={sprinkles({ marginY: 'auto' })}>
            {Illustration}
            {isIBuyerOrOdaInventory ? (
              <SuccessIBuyerOrOda />
            ) : (
              <Heading2 as="h2" className={paddingBottom['2xl']}>
                <Translated i18Key="contact-form:agency.success.message" />
              </Heading2>
            )}

            {similarPropertiesLength > 0 && (
              <Button
                center
                className={sprinkles({ marginBottom: { tablet: '2xl' } })}
                label={t(
                  isIBuyerOrOdaInventory ? 'contact-form:casavo.success.cta' : 'contact-form:agency.success.cta'
                )}
                minWidth={ctaMinWidth}
                styleVariant="secondary"
                onClick={() => {
                  onGoToSimilarProperties && onGoToSimilarProperties()
                  handleClick(
                    'ListingDetailForm',
                    `ToSimilarPropertiesFrom${isIBuyerOrOdaInventory ? 'Casavo' : 'Agency'}`
                  )
                  anchorSmoothScroll('#similarProperties', device === 'mobile' ? 0 : -100)
                }}
              />
            )}
          </div>
          <div className={sprinkles({ marginTop: 'auto' })}>
            {device === 'mobile' && <Divider />}
            <BodyM strong>
              <TokenSupport>{t('contact-form:success.toFlow.title')}</TokenSupport>
            </BodyM>
            <BodyM strong className={paddingBottom.m}>
              {t('contact-form:success.toFlow.subtitle')}
            </BodyM>

            <ButtonLink
              center
              className={sprinkles({ margin: 'auto' })}
              href={`${publicRuntimeConfig.casavoWebsite}${sellersFlowUrlPath[lang]}`}
              isNextLink={false}
              label={t('contact-form:success.toFlow.cta')}
              minWidth={ctaMinWidth}
              styleVariant="secondary"
              onClick={() => {
                handleClick('ListingDetailForm', `ToFlowFrom${isIBuyerOrOdaInventory ? 'Casavo' : 'Agency'}`)
              }}
            />
          </div>
        </>
      )}
    </motion.div>
  )
}
export default Success
